import { useState } from 'react';
import { Lock, Search } from 'lucide-react';
import { BLOCK_GROUPS, type BlockType, type Role } from '@geoic/shared';
import { Modal } from '@/components/ui/Modal.js';
import { cn } from '@/lib/cn.js';
import {
  BLOCK_GROUP_ORDER,
  BLOCK_META,
  canUseBlock,
  type BlockGroupName,
} from './block-catalog.js';

export type AddBlockModalProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (type: BlockType) => void;
  role: Role;
};

/**
 * «Blok qo'shish» modali (D4).
 *
 * 31 tur to'rt guruhga bo'lingan va qidiruv bor: xodim «slayder» deb
 * yozib topa olishi kerak — 31 ta kartochkani ko'z bilan qidirish
 * sekin va u har safar takrorlanadi.
 */
export function AddBlockModal({ open, onClose, onAdd, role }: AddBlockModalProps) {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState<BlockGroupName | 'all'>('all');

  const needle = search.trim().toLowerCase();

  const groups = BLOCK_GROUP_ORDER.filter((name) => group === 'all' || group === name).map(
    (name) => ({
      name,
      types: (BLOCK_GROUPS[name] as readonly BlockType[]).filter((type) => {
        if (!needle) return true;
        const meta = BLOCK_META[type];
        return (
          meta.label.toLowerCase().includes(needle) || meta.hint.toLowerCase().includes(needle)
        );
      }),
    }),
  );

  const found = groups.reduce((sum, item) => sum + item.types.length, 0);

  return (
    <Modal open={open} onClose={onClose} title="Blok qo‘shish" size="xl">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-n-400"
            aria-hidden
          />
          <input
            type="search"
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Blok nomi bo‘yicha qidirish"
            aria-label="Blok qidirish"
            className="h-10 w-full border border-n-200 pr-3 pl-9 text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-1">
          <GroupTab active={group === 'all'} onClick={() => setGroup('all')}>
            Hammasi
          </GroupTab>
          {BLOCK_GROUP_ORDER.map((name) => (
            <GroupTab key={name} active={group === name} onClick={() => setGroup(name)}>
              {name}
            </GroupTab>
          ))}
        </div>
      </div>

      {found === 0 && (
        <p className="py-10 text-center text-sm text-n-600">«{search}» bo‘yicha blok topilmadi</p>
      )}

      <div className="max-h-[60vh] space-y-6 overflow-y-auto">
        {groups
          .filter((item) => item.types.length > 0)
          .map((item) => (
            <section key={item.name}>
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-n-600 uppercase">
                {item.name}
              </h3>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {item.types.map((type) => {
                  const allowed = canUseBlock(type, role);
                  const meta = BLOCK_META[type];

                  return (
                    <button
                      key={type}
                      type="button"
                      disabled={!allowed}
                      onClick={() => {
                        onAdd(type);
                        onClose();
                      }}
                      className={cn(
                        ' border p-3 text-left transition-colors',
                        allowed
                          ? 'border-n-200 bg-white hover:border-steel-500 hover:bg-brand-50'
                          : 'cursor-not-allowed border-n-200 bg-n-50 opacity-60',
                      )}
                    >
                      <BlockPreview type={type} />
                      <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-n-800">
                        {meta.label}
                        {!allowed && <Lock className="size-3.5 text-n-400" aria-hidden />}
                      </p>
                      <p className="mt-0.5 text-xs text-n-600">
                        {allowed ? meta.hint : 'Faqat bosh administrator qo‘sha oladi'}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
      </div>
    </Modal>
  );
}

function GroupTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        ' px-3 py-1.5 text-sm font-medium transition-colors',
        active ? 'bg-brand-600 text-white' : 'bg-n-100 text-n-600 hover:bg-n-200',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Blokning sxematik ko'rinishi.
 *
 * Rasm emas, oddiy to'rtburchaklar: 31 ta skrinshot tayyorlash va ularni
 * dizayn o'zgarganda yangilab yurish kerak bo'lardi. Sxema esa blok
 * TUZILISHINI ko'rsatadi — xodimga aynan shu kerak.
 */
function BlockPreview({ type }: { type: BlockType }) {
  const bar = ' bg-n-200';
  const box = ' bg-n-200';

  const shape = (() => {
    switch (type) {
      case 'hero-slider':
      case 'page-header':
        return (
          <div className={cn(box, 'flex h-full flex-col justify-end gap-1 p-1.5')}>
            <span className={cn(bar, 'h-1.5 w-2/3')} />
            <span className={cn(bar, 'h-1 w-1/3')} />
          </div>
        );
      case 'text-image':
        return (
          <div className="flex h-full gap-1">
            <div className="flex flex-1 flex-col justify-center gap-1">
              <span className={cn(bar, 'h-1 w-full')} />
              <span className={cn(bar, 'h-1 w-4/5')} />
              <span className={cn(bar, 'h-1 w-3/5')} />
            </div>
            <div className={cn(box, 'w-1/3')} />
          </div>
        );
      case 'gallery':
      case 'features':
      case 'news-list':
      case 'services-list':
      case 'projects-list':
      case 'staff-list':
      case 'products-grid':
      case 'categories-grid':
      case 'droneshow-portfolio':
        return (
          <div className="grid h-full grid-cols-3 gap-1">
            {Array.from({ length: 6 }, (_, index) => (
              <span key={index} className={box} />
            ))}
          </div>
        );
      case 'stats':
        return (
          <div className="grid h-full grid-cols-4 items-center gap-1">
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="flex flex-col items-center gap-0.5">
                <span className={cn(bar, 'h-2 w-6')} />
                <span className={cn(bar, 'h-1 w-8')} />
              </div>
            ))}
          </div>
        );
      case 'faq':
      case 'accordion':
      case 'documents-list':
      case 'services-price':
        return (
          <div className="flex h-full flex-col justify-center gap-1">
            {Array.from({ length: 4 }, (_, index) => (
              <span key={index} className={cn(box, 'h-2 w-full')} />
            ))}
          </div>
        );
      case 'timeline':
        return (
          <div className="flex h-full items-center gap-1">
            <span className="h-full w-0.5 bg-n-200" />
            <div className="flex flex-1 flex-col gap-1">
              <span className={cn(bar, 'h-1.5 w-3/4')} />
              <span className={cn(bar, 'h-1.5 w-2/3')} />
              <span className={cn(bar, 'h-1.5 w-4/5')} />
            </div>
          </div>
        );
      case 'partners':
      case 'brands-strip':
      case 'product-slider':
        return (
          <div className="flex h-full items-center gap-1">
            {Array.from({ length: 5 }, (_, index) => (
              <span key={index} className={cn(box, 'h-6 flex-1')} />
            ))}
          </div>
        );
      case 'cta':
      case 'request-form':
        return (
          <div className={cn(box, 'flex h-full flex-col items-center justify-center gap-1')}>
            <span className={cn(bar, 'h-1.5 w-1/2')} />
            <span className="h-2.5 w-12 bg-steel-500" />
          </div>
        );
      case 'map':
      case 'video':
        return <div className={cn(box, 'h-full w-full')} />;
      case 'product-compare':
      case 'droneshow-packages':
        return (
          <div className="grid h-full grid-cols-3 gap-1">
            {Array.from({ length: 3 }, (_, index) => (
              <span key={index} className={cn(box, 'h-full')} />
            ))}
          </div>
        );
      case 'contact-info':
        return (
          <div className="flex h-full gap-1">
            <div className="flex flex-1 flex-col justify-center gap-1">
              <span className={cn(bar, 'h-1 w-4/5')} />
              <span className={cn(bar, 'h-1 w-3/5')} />
              <span className={cn(bar, 'h-1 w-2/3')} />
            </div>
            <span className={cn(box, 'w-1/2')} />
          </div>
        );
      case 'html':
        return (
          <div className="flex h-full flex-col justify-center gap-1 font-mono">
            <span className={cn(bar, 'h-1 w-1/3')} />
            <span className={cn(bar, 'ml-2 h-1 w-1/2')} />
            <span className={cn(bar, 'h-1 w-1/4')} />
          </div>
        );
      default:
        return (
          <div className="flex h-full flex-col justify-center gap-1">
            <span className={cn(bar, 'h-1 w-full')} />
            <span className={cn(bar, 'h-1 w-5/6')} />
            <span className={cn(bar, 'h-1 w-4/6')} />
          </div>
        );
    }
  })();

  return (
    <div aria-hidden className="h-14 border border-n-200 bg-n-50 p-1.5">
      {shape}
    </div>
  );
}
